/**
 * Platform Admin Service
 * 
 * Handles all superadmin operations via Edge Functions.
 * These operations are only available to users with is_superadmin=true in their JWT.
 */

import supabase from '../supabase/supabase';

/**
 * Bootstrap the first superadmin (platform_owner)
 * Only works when no superadmins exist
 * 
 * @param {string} userEmail - Email of the user to make superadmin
 * @param {string} secretKey - Bootstrap secret key (from environment)
 */
export const bootstrapSuperadmin = async (userEmail, secretKey) => {
  try {
    const { data, error } = await supabase.functions.invoke('api', {
      body: {
        path: 'platform/bootstrap',
        user_email: userEmail,
        secret_key: secretKey,
      },
    });

    if (error) throw error;
    if (data?.error) throw new Error(data.error);
    
    return data;
  } catch (error) {
    console.error('Error bootstrapping superadmin:', error);
    throw error;
  }
};

/**
 * Get platform metrics (aggregated counts)
 * Requires metrics:read permission
 */
export const getPlatformMetrics = async () => {
  try {
    const { data, error } = await supabase.functions.invoke('api', {
      body: {
        path: 'platform/metrics',
      },
    });

    if (error) throw error;
    if (data?.error) throw new Error(data.error);
    
    return data;
  } catch (error) {
    console.error('Error fetching platform metrics:', error);
    throw error;
  }
};

/**
 * Get audit log entries
 * Requires audit:read permission
 * 
 * @param {Object} options - Query options
 * @param {number} options.limit - Max entries to return (default 50)
 * @param {number} options.offset - Offset for pagination (default 0)
 * @param {string} options.category - Filter by action category
 */
export const getAuditLog = async (options = {}) => {
  try {
    const { data, error } = await supabase.functions.invoke('api', {
      body: {
        path: 'platform/audit',
        limit: options.limit || 50,
        offset: options.offset || 0,
        category: options.category || null,
      },
    });

    if (error) throw error;
    if (data?.error) throw new Error(data.error);
    
    return data;
  } catch (error) {
    console.error('Error fetching audit log:', error);
    throw error;
  }
};

/**
 * Check current user's superadmin status
 * Returns role and permissions
 */
export const checkSuperadminStatus = async () => {
  try {
    const { data, error } = await supabase.functions.invoke('api', {
      body: {
        path: 'platform/status',
      },
    });

    if (error) throw error;
    
    return data;
  } catch (error) {
    console.error('Error checking superadmin status:', error);
    throw error;
  }
};

// =====================================================
// TENANT MANAGEMENT
// =====================================================

/**
 * List all tenants
 * Requires tenants:read permission
 */
export const listTenants = async (options = {}) => {
  try {
    const { data, error } = await supabase.functions.invoke('api', {
      body: {
        path: 'platform/tenants',
        search: options.search || null,
        limit: options.limit || 50,
        offset: options.offset || 0,
      },
    });

    if (error) throw error;
    if (data?.error) throw new Error(data.error);
    
    return data;
  } catch (error) {
    console.error('Error listing tenants:', error);
    throw error;
  }
};

/**
 * Get single tenant details
 * Requires tenants:read permission
 */
export const getTenant = async (tenantId) => {
  try {
    const { data, error } = await supabase.functions.invoke('api', {
      body: {
        path: 'platform/tenants/get',
        tenant_id: tenantId,
      },
    });

    if (error) throw error;
    if (data?.error) throw new Error(data.error);
    
    return data;
  } catch (error) {
    console.error('Error getting tenant:', error);
    throw error;
  }
};

/**
 * Create a new tenant
 * Requires tenants:write permission
 */
export const createTenant = async (tenantData) => {
  try {
    const { data, error } = await supabase.functions.invoke('api', {
      body: {
        path: 'platform/tenants/create',
        name: tenantData.name,
        slug: tenantData.slug,
        owner_email: tenantData.ownerEmail || null,
      },
    });

    if (error) throw error;
    if (data?.error) throw new Error(data.error);
    
    return data;
  } catch (error) {
    console.error('Error creating tenant:', error);
    throw error;
  }
};

/**
 * Update tenant
 * Requires tenants:write permission
 */
export const updateTenant = async (tenantId, updates) => {
  try {
    const { data, error } = await supabase.functions.invoke('api', {
      body: {
        path: 'platform/tenants/update',
        tenant_id: tenantId,
        name: updates.name || null,
        slug: updates.slug || null,
      },
    });

    if (error) throw error;
    if (data?.error) throw new Error(data.error);
    
    return data;
  } catch (error) {
    console.error('Error updating tenant:', error);
    throw error;
  }
};

/**
 * Delete tenant (requires 2-level confirmation)
 * Requires tenants:write permission
 */
export const deleteTenant = async (tenantId, typedConfirmation) => {
  try {
    const { data, error } = await supabase.functions.invoke('api', {
      body: {
        path: 'platform/tenants/delete',
        tenant_id: tenantId,
        confirmation_level: 2,
        typed_confirmation: typedConfirmation,
      },
    });

    if (error) throw error;
    if (data?.error) throw new Error(data.error);
    
    return data;
  } catch (error) {
    console.error('Error deleting tenant:', error);
    throw error;
  }
};

// =====================================================
// USER MANAGEMENT
// =====================================================

/**
 * List all users (optionally filtered by tenant)
 * Requires users:read permission
 */
export const listUsers = async (options = {}) => {
  try {
    const { data, error } = await supabase.functions.invoke('api', {
      body: {
        path: 'platform/users',
        tenant_id: options.tenantId || null,
        search: options.search || null,
        limit: options.limit || 50,
        offset: options.offset || 0,
      },
    });

    if (error) throw error;
    if (data?.error) throw new Error(data.error);
    
    return data;
  } catch (error) {
    console.error('Error listing users:', error);
    throw error;
  }
};

/**
 * Get single user details
 * Requires users:read permission
 */
export const getUser = async (userId) => {
  try {
    const { data, error } = await supabase.functions.invoke('api', {
      body: {
        path: 'platform/users/get',
        user_id: userId,
      },
    });

    if (error) throw error;
    if (data?.error) throw new Error(data.error);
    
    return data;
  } catch (error) {
    console.error('Error getting user:', error);
    throw error;
  }
};

/**
 * Update user's tenant role (requires 2-level confirmation)
 * Requires users:write permission
 */
export const updateUserRole = async (userId, newRole) => {
  try {
    const { data, error } = await supabase.functions.invoke('api', {
      body: {
        path: 'platform/users/update-role',
        user_id: userId,
        new_role: newRole,
        confirmation_level: 2,
      },
    });

    if (error) throw error;
    if (data?.error) throw new Error(data.error);
    
    return data;
  } catch (error) {
    console.error('Error updating user role:', error);
    throw error;
  }
};

/**
 * Remove user from tenant (requires 2-level confirmation)
 * Requires users:write permission
 */
export const removeUserFromTenant = async (userId, typedConfirmation) => {
  try {
    const { data, error } = await supabase.functions.invoke('api', {
      body: {
        path: 'platform/users/remove',
        user_id: userId,
        confirmation_level: 2,
        typed_confirmation: typedConfirmation,
      },
    });

    if (error) throw error;
    if (data?.error) throw new Error(data.error);
    
    return data;
  } catch (error) {
    console.error('Error removing user:', error);
    throw error;
  }
};

// =====================================================
// MFA MANAGEMENT
// =====================================================

/**
 * Get MFA status for current superadmin
 */
export const getMfaStatus = async () => {
  try {
    const { data, error } = await supabase.functions.invoke('api', {
      body: {
        path: 'platform/mfa/status',
      },
    });

    if (error) throw error;
    if (data?.error) throw new Error(data.error);
    
    return data;
  } catch (error) {
    console.error('Error getting MFA status:', error);
    throw error;
  }
};

/**
 * Start MFA enrollment - returns QR code
 */
export const enrollMfa = async () => {
  try {
    const { data, error } = await supabase.functions.invoke('api', {
      body: {
        path: 'platform/mfa/enroll',
      },
    });

    if (error) throw error;
    if (data?.error) throw new Error(data.error);
    
    return data;
  } catch (error) {
    console.error('Error enrolling MFA:', error);
    throw error;
  }
};

/**
 * Verify MFA enrollment with TOTP code
 */
export const verifyMfaEnrollment = async (factorId, code) => {
  try {
    const { data, error } = await supabase.functions.invoke('api', {
      body: {
        path: 'platform/mfa/verify',
        factor_id: factorId,
        code: code,
      },
    });

    if (error) throw error;
    if (data?.error) throw new Error(data.error);
    
    return data;
  } catch (error) {
    console.error('Error verifying MFA:', error);
    throw error;
  }
};

/**
 * Unenroll MFA (requires current TOTP code)
 */
export const unenrollMfa = async (factorId, code) => {
  try {
    const { data, error } = await supabase.functions.invoke('api', {
      body: {
        path: 'platform/mfa/unenroll',
        factor_id: factorId,
        code: code,
      },
    });

    if (error) throw error;
    if (data?.error) throw new Error(data.error);
    
    return data;
  } catch (error) {
    console.error('Error unenrolling MFA:', error);
    throw error;
  }
};

export default {
  bootstrapSuperadmin,
  getPlatformMetrics,
  getAuditLog,
  checkSuperadminStatus,
  listTenants,
  getTenant,
  createTenant,
  updateTenant,
  deleteTenant,
  listUsers,
  getUser,
  updateUserRole,
  removeUserFromTenant,
  getMfaStatus,
  enrollMfa,
  verifyMfaEnrollment,
  unenrollMfa,
};
